import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../config/api';
import createUpdateToast from '../components/UpdateNotification';
import { toast } from 'react-hot-toast'; // 静态导入 toast

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    isDarkMode: false,
    isCompactMode: false,
  });
  
  const [notifications, setNotifications] = useState({
    browserEnabled: true,
    updatesEnabled: false,
  });
  
  const [isLoading, setIsLoading] = useState(true);

  // 从localStorage加载主题设置
  useEffect(() => {
    const loadThemeSettings = () => {
      try {
        const storedTheme = localStorage.getItem('theme-settings');
        if (storedTheme) {
          setTheme(JSON.parse(storedTheme));
        }
      } catch (error) {
        // console.error('Error loading theme settings:', error);
      }
    };

    loadThemeSettings();
  }, []);

  // 从API获取用户设置并更新主题和通知
  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsLoading(false);
          return;
        }

        const response = await api.get('/api/user/settings');
        if (response.data.success && response.data.settings) {
          // 更新主题设置
          if (response.data.settings.appearance) {
            const { darkMode, compactMode } = response.data.settings.appearance;
            
            setTheme({
              isDarkMode: darkMode || false,
              isCompactMode: compactMode || false
            });
            
            // 同步到localStorage
            localStorage.setItem('theme-settings', JSON.stringify({
              isDarkMode: darkMode || false,
              isCompactMode: compactMode || false
            }));
          }
          
          // 更新通知设置
          if (response.data.settings.notifications) {
            const { browser, updates } = response.data.settings.notifications;
            setNotifications({
              browserEnabled: browser !== undefined ? browser : true,
              updatesEnabled: updates !== undefined ? updates : false
            });
            
            // 保存通知设置
            try {
              localStorage.setItem('notification-settings', JSON.stringify({
                browser: browser !== undefined ? browser : true,
                updates: updates !== undefined ? updates : false
              }));
            } catch (e) {
              // console.error('保存通知设置失败:', e);
            }
          }
        }
      } catch (error) {
        // console.error('Error fetching user settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserSettings();
  }, []);

  // 当主题变化时应用到文档
  useEffect(() => {
    if (theme.isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    if (theme.isCompactMode) {
      document.documentElement.classList.add('compact');
    } else {
      document.documentElement.classList.remove('compact');
    }

    // 保存到localStorage
    localStorage.setItem('theme-settings', JSON.stringify(theme));
  }, [theme]);

  // 更新主题设置
  const updateTheme = (settings) => {
    setTheme(prev => ({ ...prev, ...settings }));
  };

  // 更新通知设置
  const updateNotifications = (settings) => {
    setNotifications(prev => {
      const newSettings = { ...prev, ...settings };
      
      // 保存通知设置
      try {
        localStorage.setItem('notification-settings', JSON.stringify({
          browser: newSettings.browserEnabled,
          updates: newSettings.updatesEnabled
        }));
      } catch (e) {
        // console.error('保存通知设置失败:', e);
      }
      
      return newSettings;
    });
  };

  // 显示浏览器通知
  const showBrowserNotification = async (title, options = {}) => {
    if (!notifications.browserEnabled) return null;
    
    if (!('Notification' in window)) {
      // console.log('浏览器不支持桌面通知');
      toast.error('您的浏览器不支持桌面通知');
      return null;
    }
    
    try {
      if (Notification.permission === 'granted') {
        // 权限已授予，发送通知
        const notification = new Notification(title, {
          icon: '/favicon.ico', // 使用网站图标作为默认图标
          ...options
        });
        
        notification.onclick = function() {
          window.focus();
          if (options.onClick) options.onClick();
          this.close();
        };
        
        return notification;
      }
      
      if (Notification.permission !== 'denied') {
        // 请求权限
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          const notification = new Notification(title, {
            icon: '/favicon.ico', // 使用网站图标作为默认图标
            ...options
          });
          
          notification.onclick = function() {
            window.focus();
            if (options.onClick) options.onClick();
            this.close();
          };
          
          return notification;
        } else {
          toast.error('通知权限被拒绝，请在浏览器设置中启用');
        }
      } else {
        toast.error('通知权限已被拒绝，请在浏览器设置中更改');
      }
    } catch (error) {
      // console.error('发送通知失败:', error);
      toast.error('发送通知失败: ' + error.message);
    }
    
    return null;
  };

  // 显示更新通知
  const showUpdateNotification = (update) => {
    if (!notifications.updatesEnabled) return; // 如果用户禁用了更新通知，直接返回
    
    // 使用 toast，不再使用动态导入
    toast.custom((t) => createUpdateToast(t, update), { duration: 8000 });
  };

  // 发送测试通知
  const sendTestNotification = async (type) => {
    try {
      if (type === 'browser' && notifications.browserEnabled) {
        const notification = await showBrowserNotification('测试通知', {
          body: '这是一条测试通知，表明浏览器通知功能正常工作。',
          icon: '/notification-icon.png', // 使用专门的通知图标
        });
        
        if (!notification && Notification.permission === 'granted') {
          // 如果权限已授予但无法显示通知，可能是因为其他原因
          toast.error('发送通知失败，请确认浏览器通知功能已开启');
        }
      } else if (type === 'update' && notifications.updatesEnabled) {
        showUpdateNotification({
          title: '测试产品更新通知',
          description: '这是一条测试产品更新通知，表明产品更新通知功能正常工作。',
          link: '#'
        });
      }
    } catch (error) {
      // console.error(`发送${type}通知失败:`, error);
      toast.error(`发送${type}通知失败: ${error.message}`);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        ...theme,
        ...notifications,
        updateTheme,
        updateNotifications,
        sendTestNotification,
        showBrowserNotification,
        showUpdateNotification,
        isLoading
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export default ThemeContext;
